import type { ComponentType, ReactEventHandler } from "react";

export type LandingVideoKind = "file" | "youtube" | "vimeo" | "other";

interface ReactPlayerProps {
	src?: string;
	playing?: boolean;
	muted?: boolean;
	controls?: boolean;
	playsInline?: boolean;
	preload?: string;
	width?: string | number;
	height?: string | number;
	onEnded?: () => void;
	onPlay?: () => void;
	onError?: (error: unknown) => void;
	onVolumeChange?: ReactEventHandler<HTMLVideoElement>;
	onTimeUpdate?: ReactEventHandler<HTMLVideoElement>;
}

type ReactPlayerComponent = ComponentType<ReactPlayerProps>;

export function resolveLandingVideoKind(videoUrl: string): LandingVideoKind {
	const url = videoUrl.trim();

	if (url.startsWith("/")) {
		return "file";
	}

	// Most common case: plain mp4/webm URL.
	if (/\.((mp4)|(webm)|(mov))(\?.*)?$/i.test(url)) {
		return "file";
	}

	// YouTube patterns supported by react-player.
	if (
		/(^|\/\/)(www\.)?(youtube\.com|youtu\.be)\//i.test(url) ||
		/^youtube:/i.test(url)
	) {
		return "youtube";
	}

	if (/(^|\/\/)(www\.)?vimeo\.com\//i.test(url) || /^vimeo:/i.test(url)) {
		return "vimeo";
	}

	return "other";
}

/**
 * Dynamic import for the landing hero video dialog.
 *
 * react-player v3 no longer ships `react-player/youtube` (etc.) entry points; the
 * default export registers all built-in players.
 */
export async function loadLandingVideoPlayer(
	_videoUrl: string,
): Promise<ReactPlayerComponent> {
	const mod = await import("react-player");
	return mod.default as ReactPlayerComponent;
}
